import { ContextType } from '@lit/context';
import { html, LitElement, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { pageMetadata } from '@flakiness/server/common/pageMetadata.js';
import { api } from './api.js';
import './components/audit-log.js';
import { AuditLogLoader } from './components/audit-log.js';
import { pageStyles } from './components/cssstyles.js';
import { OrganizationContext } from './components/organization-context.js';
import { consume, contexts } from './contexts.js';
import { platformUrl } from './platformUrl.js';
import { RouteConfig } from './router.js';
import { tasks } from './tasks.js';

@customElement('page-organization-audit')
export class PageOrganizationAudit extends LitElement {
  static url(options: { orgSlug: string }): string {
    return platformUrl(['', options.orgSlug, 'audit'].join('/'));
  }

  static routes(): RouteConfig[] {
    return [{
      path: [
        '/:org/audit',
      ],
      render: (groups) => OrganizationContext.render({
        orgSlug: groups?.org,
      }, html`
        <page-organization-audit .orgSlug=${groups?.org}></page-organization-audit>
      `),
    }];
  }

  @consume({ context: contexts.org, subscribe: true }) @state() private _org?: ContextType<typeof contexts.org>;
  @consume({ context: contexts.user, subscribe: true }) @state() private _user?: ContextType<typeof contexts.user>;
  @property({ attribute: false }) orgSlug?: string;

  private _loader: AuditLogLoader = async (pageOptions, signal) => {
    const orgSlug = this.orgSlug;
    if (!orgSlug)
      return { elements: [], pageNumber: pageOptions.number, pageSize: pageOptions.size, totalElements: 0, totalPages: 0 };
    return await api.organization.auditLog.GET({
      orgSlug,
      page: pageOptions.number,
      pageSize: pageOptions.size,
    }, { signal });
  };

  private _pageMetadataTask = tasks.pageMetadata(this, {
    args: () => [this.orgSlug] as const,
    metadata: ([orgSlug]) => orgSlug ? pageMetadata({ orgSlug, section: 'Audit Log', accessGated: true }) : undefined,
  });

  override render() {
    if (!this._user)
      return nothing;
    const isOrgAdmin = this._org?.access === 'admin' || this._org?.access === 'owner';
    const canView = this._user.isSuperUser || (isOrgAdmin && !!this._org?.eliteFeatures);
    if (!canView)
      return html`<page-http-error code=403></page-http-error>`;
    return html`
      <org-header .orgSlug=${this.orgSlug} submenu=audit></org-header>
      <app-body wide user-role=${this._org?.access}>
        <h1>Audit log</h1>
        <audit-log .loader=${this._loader}></audit-log>
      </app-body>
    `;
  }

  static styles = [pageStyles];
}
